<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">{{ $t("dmp.pcLogReport.title") }}</h5>
            <div class="d-flex gap-1">
              <b-button v-if="!isTemplateMode" variant="primary" size="sm" @click="savePcLogReport" :disabled="isSaving">
                <b-spinner v-if="isSaving" small class="mr-25" />
                <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                {{ isSaving ? $t("actions.saving") : $t("actions.save") }}
              </b-button>
            </div>
          </div>

          <!-- Loading spinner -->
          <div v-if="isLoading" class="d-flex justify-content-center p-3">
            <b-spinner variant="primary" :label="$t('dmp.pcLogReport.loading')" />
            <span class="ml-2">{{ $t("dmp.pcLogReport.loading") }}...</span>
          </div>

          <b-form v-else @submit.prevent="savePcLogReport">
            <b-row>

              <!-- Options (checkboxes) -->
              <b-col cols="12" md="6">
                <b-card class="mb-2 border h-100">
                  <h6 class="mb-3 font-weight-bold">
                    {{ $t("dmp.pcLogReport.optionsSection") }}
                  </h6>

                  <b-row>
                    <!-- Arm/Disarm Reports -->
                    <b-col cols="12" class="mb-2">
                      <b-form-checkbox
                        id="arm-disarm-reports"
                        v-model="pcLogReport.armDisarmReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.pcLogReport.armDisarmReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Zone Reports -->
                    <b-col cols="12" class="mb-2">
                      <b-form-checkbox
                        id="zone-reports"
                        v-model="pcLogReport.zoneReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.pcLogReport.zoneReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- User Command Reports -->
                    <b-col cols="12" class="mb-2">
                      <b-form-checkbox
                        id="use-command-reports"
                        v-model="pcLogReport.useCommandReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.pcLogReport.useCommandReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Door Access Reports -->
                    <b-col cols="12" class="mb-2">
                      <b-form-checkbox
                        id="door-access-reports"
                        v-model="pcLogReport.doorAccessReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.pcLogReport.doorAccessReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Supervisory Reports -->
                    <b-col cols="12" class="mb-2">
                      <b-form-checkbox
                        id="supervisory-reports"
                        v-model="pcLogReport.supervisoryReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.pcLogReport.supervisoryReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Real-time Status -->
                    <b-col cols="12" class="mb-2">
                      <b-form-checkbox
                        id="real-time-status"
                        v-model="pcLogReport.realTimeStatus"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.pcLogReport.realTimeStatus") }}
                      </b-form-checkbox>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>

              <!-- Net Options -->
              <b-col cols="12" md="6">
                <b-card class="mb-2 border h-100">
                  <h6 class="mb-3 font-weight-bold">
                    {{ $t("dmp.pcLogReport.netOptionsSection") }}
                  </h6>

                  <b-row>
                    <!-- Net IP Address -->
                    <b-col cols="12" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.pcLogReport.netIpAddress')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.pcLogReport.netIpAddress')"
                          label-for="net-ip-address"
                        >
                          <b-form-input
                            id="net-ip-address"
                            v-model="pcLogReport.netIpAddress"
                            :state="getValidationState(validationContext)"
                            placeholder="0.0.0.0"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Net Port -->
                    <b-col cols="12" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.pcLogReport.netPort')"
                        rules="required|integer|min_value:1|max_value:65535"
                      >
                        <b-form-group
                          :label="$t('dmp.pcLogReport.netPort')"
                          label-for="net-port"
                        >
                          <b-form-input
                            id="net-port"
                            v-model="pcLogReport.netPort"
                            :state="getValidationState(validationContext)"
                            placeholder="2001"
                            maxlength="5"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>

            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, inject, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "DmpPanelsConfigPcLogReport",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    pcLogReportData: {
      type: Object,
      default: () => ({}),
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    const { refFormObserver, getValidationState } = formValidation();

    const isSaving = ref(false);
    const isLoading = ref(false);

    const pcLogReport = ref({
      armDisarmReports:   "N",
      doorAccessReports:  "N",
      netIpAddress:       "0.0.0.0",
      netPort:            "2001",
      realTimeStatus:     "N",
      supervisoryReports: "N",
      useCommandReports:  "N",
      zoneReports:        "N",
    });

    const loadPcLogReport = () => {
      if (props.pcLogReportData && Object.keys(props.pcLogReportData).length > 0) {
        pcLogReport.value = {
          armDisarmReports:   props.pcLogReportData.armDisarmReports   ?? "N",
          doorAccessReports:  props.pcLogReportData.doorAccessReports  ?? "N",
          netIpAddress:       (props.pcLogReportData.netIpAddress ?? "0.0.0.0").trim(),
          netPort:            props.pcLogReportData.netPort            ?? "2001",
          realTimeStatus:     props.pcLogReportData.realTimeStatus     ?? "N",
          supervisoryReports: props.pcLogReportData.supervisoryReports ?? "N",
          useCommandReports:  props.pcLogReportData.useCommandReports  ?? "N",
          zoneReports:        props.pcLogReportData.zoneReports        ?? "N",
        };
      }
    };
    
    // Construye la sección en forma GET para el template.
    const buildTemplateItem = () => ({
      ...(props.pcLogReportData || {}),
      armDisarmReports:   pcLogReport.value.armDisarmReports,
      doorAccessReports:  pcLogReport.value.doorAccessReports,
      netIpAddress:       pcLogReport.value.netIpAddress.trim(),
      netPort:            pcLogReport.value.netPort,
      realTimeStatus:     pcLogReport.value.realTimeStatus,
      supervisoryReports: pcLogReport.value.supervisoryReports,
      useCommandReports:  pcLogReport.value.useCommandReports,
      zoneReports:        pcLogReport.value.zoneReports,
    });

    const validateTemplateSection = async () =>
      refFormObserver.value ? await refFormObserver.value.validate() : true;

    const savePcLogReport = async () => {
      const isValid = await refFormObserver.value.validate();
      if (!isValid) return;

      if (isTemplateMode.value) {
        emitTemplateUpdate("pc_log_report", buildTemplateItem());
        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.success"),
            text:    proxy.$t("dmp.pcLogReport.savedSuccessfully"),
            icon:    "CheckIcon",
            variant: "success",
          },
        });
        return;
      }

      isSaving.value = true;

      try {
        const payload = {
          armDisarmReports:   pcLogReport.value.armDisarmReports,
          doorAccessReports:  pcLogReport.value.doorAccessReports,
          netIpAddress:       pcLogReport.value.netIpAddress.trim(),
          netPort:            pcLogReport.value.netPort,
          realTimeStatus:     pcLogReport.value.realTimeStatus,
          supervisoryReports: pcLogReport.value.supervisoryReports,
          useCommandReports:  pcLogReport.value.useCommandReports,
          zoneReports:        pcLogReport.value.zoneReports,
        };

        await store.dispatch("dmp_panels/setPcLogReport", {
          uuid: props.uuid,
          pcLogReportData: payload,
        });

        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.success"),
            text:    proxy.$t("dmp.pcLogReport.savedSuccessfully"),
            icon:    "CheckIcon",
            variant: "success",
          },
        });

        emit("operation-completed");
      } catch (error) {
        console.error("Error saving PC log report:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.error"),
            text:    proxy.$t("dmp.pcLogReport.errorSaving"),
            icon:    "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSaving.value = false;
      }
    };

    watch(
      () => props.pcLogReportData,
      (newData) => {
        if (newData && Object.keys(newData).length > 0) {
          loadPcLogReport();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadPcLogReport();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("pc_log_report", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("pc_log_report");
      }
    });

    return {
      refFormObserver,
      getValidationState,
      isSaving,
      isLoading,
      pcLogReport,
      savePcLogReport,
      isTemplateMode,
    };
  },
};
</script>

<style lang="scss" scoped>
.border {
  border: 1px solid #ebe9f1 !important;
}

.dark-layout {
  .border {
    border-color: #404656 !important;
  }
}
</style>